import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Checkbox,
  IconButton,
  Chip,
  Avatar,
  AvatarGroup,
  Typography,
  Menu,
  MenuItem,
  LinearProgress,
  Stack,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
  Badge,
  Snackbar,
  Alert
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  DragIndicator as DragIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  PlayCircle as InProgressIcon,
  PauseCircle as BlockedIcon,
  Cancel as CancelledIcon,
  Comment as CommentIcon,
  AttachFile as AttachmentIcon,
  SubdirectoryArrowRight as SubtaskIcon
} from '@mui/icons-material';
import { format, isBefore, startOfDay } from 'date-fns';
import { Task, BulkAction } from '../../services/api/todo.service';
import { useTaskEvents } from '../../hooks/useTaskEvents';
import { TaskEvent } from '../../services/api/task-events.service';

export type ViewMode = 'list' | 'grid' | 'table';
export type SortField = 'title' | 'status' | 'priority' | 'due_date' | 'created_at' | 'updated_at';
export type SortOrder = 'asc' | 'desc';

export interface FilterOptions {
  status?: Task['status'][];
  priority?: Task['priority'][];
  assignee?: string[];
  project?: string[];
  labels?: string[];
  dateRange?: { start: Date; end: Date };
}

interface TaskListViewProps {
  tasks: Task[];
  viewMode: ViewMode;
  sortBy: SortField;
  sortOrder: SortOrder;
  filterOptions: FilterOptions;
  onTaskClick: (taskId: string) => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => void;
  onBulkAction: (taskIds: string[], action: BulkAction, data?: any) => void;
  selectedTasks: Set<string>;
  onSelectionChange: (taskIds: Set<string>) => void;
  onSortChange?: (field: SortField, order: SortOrder) => void;
  enableDragDrop?: boolean;
  onTaskReorder?: (taskId: string, newPosition: number) => void;
  onRealTimeTaskUpdate?: (taskId: string, eventType: string, taskData: any) => void;
  enableRealTimeUpdates?: boolean;
}

const TaskListView: React.FC<TaskListViewProps> = ({
  tasks,
  viewMode,
  sortBy,
  sortOrder,
  filterOptions,
  onTaskClick,
  onTaskUpdate,
  onBulkAction,
  selectedTasks,
  onSelectionChange,
  onSortChange,
  enableDragDrop = false,
  onTaskReorder,
  onRealTimeTaskUpdate,
  enableRealTimeUpdates = true
}) => {
  const theme = useTheme();
  const [contextMenuTask, setContextMenuTask] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [starredTasks, setStarredTasks] = useState<Set<string>>(new Set());
  const [realtimeNotifications, setRealtimeNotifications] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({ open: false, message: '', severity: 'info' });
  const [highlightedTasks, setHighlightedTasks] = useState<Set<string>>(new Set());

  // Real-time task events integration
  const { isConnected, isReconnecting } = useTaskEvents({
    onTaskCreated: (event: TaskEvent) => {
      if (!enableRealTimeUpdates) return;

      const taskData = event.data;
      console.log('[TaskListView] Task created event:', taskData);

      // Highlight the new task briefly
      if ('taskId' in taskData && taskData.taskId) {
        setHighlightedTasks(prev => new Set([...Array.from(prev), taskData.taskId]));
        setTimeout(() => {
          setHighlightedTasks(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskData.taskId);
            return newSet;
          });
        }, 3000);
      }

      // Show notification
      setRealtimeNotifications({
        open: true,
        message: `New task created: ${('data' in taskData && taskData.data?.task?.title) || 'Untitled'}`,
        severity: 'success'
      });

      // Notify parent component
      if (onRealTimeTaskUpdate && 'taskId' in taskData) {
        onRealTimeTaskUpdate(taskData.taskId, event.type, taskData);
      }
    },

    onTaskUpdated: (event: TaskEvent) => {
      if (!enableRealTimeUpdates) return;

      const taskData = event.data;
      console.log('[TaskListView] Task updated event:', taskData);

      // Highlight the updated task briefly
      if ('taskId' in taskData && taskData.taskId) {
        setHighlightedTasks(prev => new Set([...Array.from(prev), taskData.taskId]));
        setTimeout(() => {
          setHighlightedTasks(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskData.taskId);
            return newSet;
          });
        }, 2000);
      }

      // Show notification for significant updates
      const task = 'data' in taskData && taskData.data?.task;
      if (task) {
        setRealtimeNotifications({
          open: true,
          message: `Task updated: ${task.title || 'Untitled'}`,
          severity: 'info'
        });
      }

      // Notify parent component
      if (onRealTimeTaskUpdate && 'taskId' in taskData) {
        onRealTimeTaskUpdate(taskData.taskId, event.type, taskData);
      }
    },

    onTaskStatusChanged: (event: TaskEvent) => {
      if (!enableRealTimeUpdates) return;

      const taskData = event.data;
      console.log('[TaskListView] Task status changed event:', taskData);

      // Highlight the task with status change
      if ('taskId' in taskData && taskData.taskId) {
        setHighlightedTasks(prev => new Set([...Array.from(prev), taskData.taskId]));
        setTimeout(() => {
          setHighlightedTasks(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskData.taskId);
            return newSet;
          });
        }, 2000);
      }

      // Show status change notification
      const task = 'data' in taskData && taskData.data?.task;
      const newStatus = task?.status;
      if (task && newStatus) {
        const statusMessages: Record<string, string> = {
          completed: 'completed',
          in_progress: 'started',
          blocked: 'blocked',
          cancelled: 'cancelled',
          pending: 'reset to pending'
        };

        setRealtimeNotifications({
          open: true,
          message: `${task.title || 'Task'} ${statusMessages[newStatus] || `status: ${newStatus}`}`,
          severity: newStatus === 'completed' ? 'success' : 'info'
        });
      }

      // Notify parent component
      if (onRealTimeTaskUpdate && 'taskId' in taskData) {
        onRealTimeTaskUpdate(taskData.taskId, event.type, taskData);
      }
    },

    onTaskDeleted: (event: TaskEvent) => {
      if (!enableRealTimeUpdates) return;

      const taskData = event.data;
      console.log('[TaskListView] Task deleted event:', taskData);

      // Show deletion notification
      setRealtimeNotifications({
        open: true,
        message: `Task deleted: ${('data' in taskData && taskData.data?.task?.title) || 'Untitled'}`,
        severity: 'warning'
      });

      // Notify parent component
      if (onRealTimeTaskUpdate && 'taskId' in taskData) {
        onRealTimeTaskUpdate(taskData.taskId, event.type, taskData);
      }
    },

    onTaskAssigned: (event: TaskEvent) => {
      if (!enableRealTimeUpdates) return;

      const taskData = event.data;
      console.log('[TaskListView] Task assigned event:', taskData);

      // Show assignment notification
      const task = 'data' in taskData && taskData.data?.task;
      if (task) {
        setRealtimeNotifications({
          open: true,
          message: `Task assigned: ${task.title || 'Untitled'}`,
          severity: 'info'
        });
      }

      // Notify parent component
      if (onRealTimeTaskUpdate && 'taskId' in taskData) {
        onRealTimeTaskUpdate(taskData.taskId, event.type, taskData);
      }
    },

    onConnected: () => {
      console.log('[TaskListView] Real-time connection established');
    },

    onError: (error: Error) => {
      console.error('[TaskListView] Real-time connection error:', error);
      setRealtimeNotifications({
        open: true,
        message: 'Real-time updates temporarily unavailable',
        severity: 'error'
      });
    },

    autoConnect: enableRealTimeUpdates
  });

  // Close notification handler
  const handleCloseNotification = () => {
    setRealtimeNotifications(prev => ({ ...prev, open: false }));
  };

  // Status icons and colors
  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'in_progress':
        return <InProgressIcon fontSize="small" color="info" />;
      case 'blocked':
        return <BlockedIcon fontSize="small" color="error" />;
      case 'cancelled':
        return <CancelledIcon fontSize="small" color="disabled" />;
      default:
        return <PendingIcon fontSize="small" color="action" />;
    }
  };

  // Priority colors
  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent':
        return '#d32f2f';
      case 'high':
        return '#ed6c02';
      case 'medium':
        return theme.palette.info.main;
      default:
        return 'rgba(0,0,0,0.6)';
    }
  };

  // Check if task is overdue
  const isOverdue = (task: Task) => {
    if (!task.due_date || task.status === 'completed') return false;
    return isBefore(new Date(task.due_date), startOfDay(new Date()));
  };

  // Handle selection
  const handleSelectAll = () => {
    if (selectedTasks.size === tasks.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(tasks.map(t => t.id)));
    }
  };

  const handleSelectTask = (taskId: string) => {
    const newSelection = new Set(selectedTasks);
    if (newSelection.has(taskId)) {
      newSelection.delete(taskId);
    } else {
      newSelection.add(taskId);
    }
    onSelectionChange(newSelection);
  };

  // Handle context menu
  const handleContextMenu = (event: React.MouseEvent, taskId: string) => {
    event.preventDefault();
    setContextMenuTask(taskId);
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleCloseContextMenu = () => {
    setContextMenuTask(null);
    setAnchorEl(null);
  };

  // Handle star toggle
  const toggleStar = (taskId: string) => {
    const newStarred = new Set(starredTasks);
    if (newStarred.has(taskId)) {
      newStarred.delete(taskId);
    } else {
      newStarred.add(taskId);
    }
    setStarredTasks(newStarred);
    // In a real app, this would also update the backend
    onTaskUpdate(taskId, { metadata: { starred: newStarred.has(taskId) } });
  };

  // Handle quick status update
  const handleQuickStatusUpdate = (taskId: string, status: Task['status']) => {
    onTaskUpdate(taskId, { status });
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange?.(field, newOrder);
  };

  // Render task row for table view
  const renderTableRow = (task: Task) => {
    const isSelected = selectedTasks.has(task.id);
    const isTaskOverdue = isOverdue(task);
    const isHighlighted = highlightedTasks.has(task.id);

    return (
      <TableRow
        key={task.id}
        hover
        selected={isSelected}
        onClick={() => onTaskClick(task.id)}
        onContextMenu={(e) => handleContextMenu(e, task.id)}
        style={{
          cursor: 'pointer',
          transition: 'background-color 0.3s ease',
          ...(isHighlighted && {
            backgroundColor: alpha('#2e7d32', 0.15)
          })
        }}
      >
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onChange={() => handleSelectTask(task.id)}
          />
        </TableCell>
        {enableDragDrop && (
          <TableCell padding="none" style={{ width: 40 }}>
            <DragIcon style={{ cursor: 'grab' }} />
          </TableCell>
        )}
        <TableCell>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleStar(task.id);
              }}
            >
              {starredTasks.has(task.id) ?
                <StarIcon fontSize="small" color="warning" /> :
                <StarBorderIcon fontSize="small" />
              }
            </IconButton>
            {getStatusIcon(task.status)}
            <Typography
              variant="body2"
              style={{
                textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                color: isTaskOverdue ? '#d32f2f' : 'inherit'
              }}
            >
              {task.title}
            </Typography>
            {task.subtasks && task.subtasks.length > 0 && (
              <Chip
                icon={<SubtaskIcon />}
                label={task.subtasks.length}
                size="small"
                variant="outlined"
              />
            )}
          </div>
        </TableCell>
        <TableCell>
          <Chip
            label={task.priority}
            size="small"
            style={{
              backgroundColor: alpha(getPriorityColor(task.priority), 0.1),
              color: getPriorityColor(task.priority)
            }}
          />
        </TableCell>
        <TableCell>{task.project_name || '-'}</TableCell>
        <TableCell>
          {task.assigned_teammate_name ? (
            <Chip
              label={`@ ${task.assigned_teammate_name}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell>
          {task.assignee_ids && task.assignee_ids.length > 0 && (
            <AvatarGroup max={3} style={{ justifyContent: 'flex-start' }}>
              {task.assignee_ids.map((id) => (
                <Avatar key={id} style={{ width: 24, height: 24 }}>
                  {id[0].toUpperCase()}
                </Avatar>
              ))}
            </AvatarGroup>
          )}
        </TableCell>
        <TableCell>
          {task.due_date && (
            <Chip
              icon={<CalendarIcon />}
              label={format(new Date(task.due_date), 'MMM dd')}
              size="small"
              color={isTaskOverdue ? 'error' : 'default'}
              variant={isTaskOverdue ? 'filled' : 'outlined'}
            />
          )}
        </TableCell>
        <TableCell>
          {task.progress !== undefined && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LinearProgress
                variant="determinate"
                value={task.progress}
                style={{ width: 60, height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption">{task.progress}%</Typography>
            </div>
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5}>
            {task.comments && task.comments.length > 0 && (
              <Chip
                icon={<CommentIcon />}
                label={task.comments.length}
                size="small"
                variant="outlined"
              />
            )}
            {task.attachments && task.attachments.length > 0 && (
              <Chip
                icon={<AttachmentIcon />}
                label={task.attachments.length}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleContextMenu(e, task.id);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  // Render task card for grid view
  const renderGridCard = (task: Task) => {
    const isSelected = selectedTasks.has(task.id);
    const isTaskOverdue = isOverdue(task);
    const isHighlighted = highlightedTasks.has(task.id);

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={task.id}>
        <Card
          style={{
            cursor: 'pointer',
            border: isHighlighted
              ? `2px solid #2e7d32`
              : isSelected
              ? '2px solid #325BA8'
              : '1px solid rgba(0,0,0,0.12)',
            transition: 'all 0.3s ease',
            ...(isHighlighted && {
              backgroundColor: alpha('#2e7d32', 0.08)
            })
          }}
          onClick={() => onTaskClick(task.id)}
          onContextMenu={(e) => handleContextMenu(e, task.id)}
        >
          <CardContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleSelectTask(task.id)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleStar(task.id);
                  }}
                >
                  {starredTasks.has(task.id) ?
                    <StarIcon fontSize="small" color="warning" /> :
                    <StarBorderIcon fontSize="small" />
                  }
                </IconButton>
              </div>
              {getStatusIcon(task.status)}
            </div>

            <Typography
              variant="body1"
              gutterBottom
              style={{
                textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                color: isTaskOverdue ? '#d32f2f' : 'inherit'
              }}
            >
              {task.title}
            </Typography>

            {task.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  marginBottom: 8
                }}
              >
                {task.description}
              </Typography>
            )}

            <Stack direction="row" spacing={1} style={{ marginBottom: 8 }}>
              <Chip
                label={task.priority}
                size="small"
                style={{
                  backgroundColor: alpha(getPriorityColor(task.priority), 0.1),
                  color: getPriorityColor(task.priority)
                }}
              />
              {task.assigned_teammate_name && (
                <Chip
                  label={`@ ${task.assigned_teammate_name}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {task.project_name && (
                <Chip
                  label={task.project_name}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>

            {task.due_date && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                <CalendarIcon fontSize="small" color={isTaskOverdue ? 'error' : 'action'} />
                <Typography variant="caption" color={isTaskOverdue ? 'error' : 'text.secondary'}>
                  {format(new Date(task.due_date), 'MMM dd, yyyy')}
                </Typography>
              </div>
            )}

            {task.progress !== undefined && (
              <div style={{ marginBottom: 8 }}>
                <LinearProgress
                  variant="determinate"
                  value={task.progress}
                  style={{ height: 6, borderRadius: 3 }}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {task.assignee_ids && task.assignee_ids.length > 0 && (
                <AvatarGroup max={3} style={{ justifyContent: 'flex-start' }}>
                  {task.assignee_ids.map((id) => (
                    <Avatar key={id} style={{ width: 24, height: 24 }}>
                      {id[0].toUpperCase()}
                    </Avatar>
                  ))}
                </AvatarGroup>
              )}
              <Stack direction="row" spacing={0.5}>
                {task.comments && task.comments.length > 0 && (
                  <Chip
                    icon={<CommentIcon />}
                    label={task.comments.length}
                    size="small"
                    variant="outlined"
                  />
                )}
                {task.attachments && task.attachments.length > 0 && (
                  <Chip
                    icon={<AttachmentIcon />}
                    label={task.attachments.length}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </div>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // Render list item for list view
  const renderListItem = (task: Task) => {
    const isSelected = selectedTasks.has(task.id);
    const isTaskOverdue = isOverdue(task);
    const isHighlighted = highlightedTasks.has(task.id);

    return (
      <Paper
        key={task.id}
        style={{
          padding: 16,
          marginBottom: 8,
          cursor: 'pointer',
          border: isHighlighted
            ? '2px solid #2e7d32'
            : isSelected
            ? '2px solid #325BA8'
            : '1px solid rgba(0,0,0,0.12)',
          transition: 'all 0.3s ease',
          ...(isHighlighted && {
            backgroundColor: alpha('#2e7d32', 0.08)
          })
        }}
        onClick={() => onTaskClick(task.id)}
        onContextMenu={(e) => handleContextMenu(e, task.id)}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {enableDragDrop && <DragIcon style={{ cursor: 'grab' }} />}
            <Checkbox
              checked={isSelected}
              onChange={() => handleSelectTask(task.id)}
              onClick={(e) => e.stopPropagation()}
            />
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleStar(task.id);
              }}
            >
              {starredTasks.has(task.id) ?
                <StarIcon fontSize="small" color="warning" /> :
                <StarBorderIcon fontSize="small" />
              }
            </IconButton>
            {getStatusIcon(task.status)}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Typography
                variant="body1"
                style={{
                  textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                  color: isTaskOverdue ? '#d32f2f' : 'inherit'
                }}
              >
                {task.title}
              </Typography>
              <Chip
                label={task.priority}
                size="small"
                style={{
                  backgroundColor: alpha(getPriorityColor(task.priority), 0.1),
                  color: getPriorityColor(task.priority)
                }}
              />
              {task.assigned_teammate_name && (
                <Chip
                  label={`@ ${task.assigned_teammate_name}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {task.project_name && (
                <Chip
                  label={task.project_name}
                  size="small"
                  variant="outlined"
                />
              )}
            </div>

            {task.description && (
              <Typography variant="body2" color="text.secondary" style={{ marginBottom: 8 }}>
                {task.description}
              </Typography>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {task.due_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CalendarIcon fontSize="small" color={isTaskOverdue ? 'error' : 'action'} />
                  <Typography variant="caption" color={isTaskOverdue ? 'error' : 'text.secondary'}>
                    {format(new Date(task.due_date), 'MMM dd')}
                  </Typography>
                </div>
              )}

              {task.assignee_ids && task.assignee_ids.length > 0 && (
                <AvatarGroup max={3} style={{ justifyContent: 'flex-start' }}>
                  {task.assignee_ids.map((id) => (
                    <Avatar key={id} style={{ width: 20, height: 20 }}>
                      {id[0].toUpperCase()}
                    </Avatar>
                  ))}
                </AvatarGroup>
              )}

              {task.progress !== undefined && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LinearProgress
                    variant="determinate"
                    value={task.progress}
                    style={{ width: 60, height: 4, borderRadius: 2 }}
                  />
                  <Typography variant="caption">{task.progress}%</Typography>
                </div>
              )}

              <Stack direction="row" spacing={0.5}>
                {task.subtasks && task.subtasks.length > 0 && (
                  <Chip
                    icon={<SubtaskIcon />}
                    label={task.subtasks.length}
                    size="small"
                    variant="outlined"
                  />
                )}
                {task.comments && task.comments.length > 0 && (
                  <Chip
                    icon={<CommentIcon />}
                    label={task.comments.length}
                    size="small"
                    variant="outlined"
                  />
                )}
                {task.attachments && task.attachments.length > 0 && (
                  <Chip
                    icon={<AttachmentIcon />}
                    label={task.attachments.length}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </div>
          </div>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleContextMenu(e, task.id);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </div>
      </Paper>
    );
  };

  // Context menu
  const renderContextMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleCloseContextMenu}
    >
      <MenuItem onClick={() => {
        if (contextMenuTask) {
          onTaskClick(contextMenuTask);
        }
        handleCloseContextMenu();
      }}>
        Open
      </MenuItem>
      <MenuItem onClick={() => {
        if (contextMenuTask) {
          handleQuickStatusUpdate(contextMenuTask, 'completed');
        }
        handleCloseContextMenu();
      }}>
        Mark Complete
      </MenuItem>
      <MenuItem onClick={() => {
        if (contextMenuTask) {
          handleQuickStatusUpdate(contextMenuTask, 'in_progress');
        }
        handleCloseContextMenu();
      }}>
        Mark In Progress
      </MenuItem>
      <MenuItem onClick={() => {
        if (contextMenuTask) {
          onBulkAction([contextMenuTask], 'delete');
        }
        handleCloseContextMenu();
      }}>
        Delete
      </MenuItem>
    </Menu>
  );

  // Main render
  return (
    <div>
      {viewMode === 'table' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedTasks.size > 0 && selectedTasks.size < tasks.length}
                    checked={tasks.length > 0 && selectedTasks.size === tasks.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                {enableDragDrop && <TableCell padding="none" style={{ width: 40 }} />}
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'title'}
                    direction={sortBy === 'title' ? sortOrder : 'asc'}
                    onClick={() => handleSort('title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'priority'}
                    direction={sortBy === 'priority' ? sortOrder : 'asc'}
                    onClick={() => handleSort('priority')}
                  >
                    Priority
                  </TableSortLabel>
                </TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Assigned Teammate</TableCell>
                <TableCell>Assignees</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'due_date'}
                    direction={sortBy === 'due_date' ? sortOrder : 'asc'}
                    onClick={() => handleSort('due_date')}
                  >
                    Due Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map(renderTableRow)}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {viewMode === 'grid' && (
        <Grid container spacing={2}>
          {tasks.map(renderGridCard)}
        </Grid>
      )}

      {viewMode === 'list' && (
        <div>
          {tasks.map(renderListItem)}
        </div>
      )}

      {renderContextMenu()}

      {/* Real-time notifications */}
      <Snackbar
        open={realtimeNotifications.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={realtimeNotifications.severity}
          variant="filled"
          style={{ width: '100%' }}
        >
          {realtimeNotifications.message}
        </Alert>
      </Snackbar>

      {/* Connection status indicator (subtle, only shown when disconnected) */}
      {enableRealTimeUpdates && !isConnected && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1300,
            backgroundColor: isReconnecting ? '#ed6c02' : '#d32f2f',
            color: 'white',
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 8,
            paddingBottom: 8,
            borderRadius: 4,
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <Badge
            variant="dot"
          />
          {isReconnecting ? 'Reconnecting...' : 'Real-time updates offline'}
        </div>
      )}
    </div>
  );
};

export default TaskListView;
